import QRCode from 'qrcode';

/**
 * Etiquetas QR de club: el papel que se pega en la entrada de cada club para que el
 * juez salte directo a calificarlo sin buscarlo en una lista de 129.
 *
 * El contenido del QR es `CMC1:<campId>:<clubId>` en mayúsculas. El contrato vive en
 * `mobile-android/.../utils/ClubQrPayload.kt` (con su test); aquí solo se replica el
 * lado emisor. Si cambia allá el prefijo o la versión, cambia también aquí.
 *
 * El móvil resuelve el escaneo contra Room, sin red. Por eso las etiquetas tienen que
 * salir del mismo entorno del que come la app: ids de producción para APK de
 * producción.
 *
 * Este módulo es solo strings: no toca el DOM ni React. La hoja se imprime abriendo el
 * HTML que devuelve `construirHojaEtiquetas` en una ventana aparte.
 */

const PREFIJO = 'CMC';
const VERSION = '1';

const VERDE = '#284B32';
const CREMA = '#f4f1e9';

/** Etiquetas por hoja A4. Tres columnas por tres filas, con sitio para tres líneas de texto. */
const POR_HOJA = 9;

/**
 * En mayúsculas a propósito: así el codificador usa el modo alfanumérico
 * —`0-9 A-Z $ % * + - . / :`— en vez de byte, y el símbolo sale más chico para el
 * mismo contenido. `ClubQrPayload.parse` los baja a minúscula antes de validar.
 */
export function construirPayload(campId, clubId) {
  return `${PREFIJO}${VERSION}:${campId}:${clubId}`.toUpperCase();
}

const escapar = valor =>
  String(valor ?? '').replace(/[&<>"]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));

/** "ADE · Zona 2 - La Romana", con lo que haya. La zona sola si el nombre no viene. */
export function textoAfiliacion(club) {
  const afiliacion = typeof club.affiliatedTo === 'object' && club.affiliatedTo !== null ? club.affiliatedTo : null;
  return [afiliacion?.name, afiliacion?.zone].filter(Boolean).join(' · ');
}

/**
 * El logo del campamento vive en Cloudinary a tamaño original, que para un cuadro de
 * 11 mm es tirar ancho de banda: pedirlo recortado a 240 px acelera la impresión de 15
 * hojas. Si la URL no es de Cloudinary (o cambia de forma), se devuelve intacta.
 */
export function logoParaImpresion(logoUrl) {
  if (!logoUrl) return '';
  return logoUrl.replace('/image/upload/', '/image/upload/w_240,h_240,c_fit,q_auto/');
}

/**
 * Nivel H (30% de redundancia) cuando hay logo, porque el logo tapa el centro del
 * símbolo; sin logo basta Q y el patrón queda más grueso, que se lee mejor de lejos.
 */
export function generarQrSvg(payload, { conLogo = false } = {}) {
  return QRCode.toString(payload, {
    errorCorrectionLevel: conLogo ? 'H' : 'Q',
    margin: 0,
    type: 'svg',
  });
}

/**
 * Una etiqueta: nombre del club en el verde de la app, afiliación, campamento y el QR
 * con el logo del campamento al centro.
 */
export function construirEtiqueta({ club, campName, qrSvg, logoUrl }) {
  const inactivo = club.status === false ? ' · <span class="off">Inactivo</span>' : '';
  const afiliacion = textoAfiliacion(club);
  const logo = logoUrl
    ? `<span class="logo"><img src="${escapar(logoUrl)}" alt=""></span>`
    : '';

  return `
      <article class="label">
        <h2 class="club">${escapar(club.name)}</h2>
        <p class="aff">${escapar(afiliacion) || '&nbsp;'}${inactivo}</p>
        <p class="camp">${escapar(campName)}</p>
        <div class="qr">${qrSvg.replace('<svg ', '<svg class="sym" ')}${logo}</div>
        <p class="code">${escapar(club._id.slice(-6).toUpperCase())}</p>
      </article>`;
}

const ESTILOS = `
  @page { size: A4 portrait; margin: 10mm; }
  * { box-sizing: border-box; }
  body { margin: 0; background: ${CREMA}; color: #1b1b1b;
         font-family: 'Avenir Next', 'Trebuchet MS', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
         -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .sheet { width: 190mm; min-height: 277mm; margin: 0 auto; background: #fff; }
  .sheet + .sheet { page-break-before: always; }
  .sheet-head { display: flex; justify-content: flex-end; align-items: baseline;
                border-bottom: 1px solid rgba(40, 75, 50, 0.15); padding-bottom: 2.5mm; margin-bottom: 4mm; }
  .meta { font-size: 7pt; color: rgba(0,0,0,0.38); }
  .grid { display: grid; grid-template-columns: repeat(3, 1fr); grid-auto-rows: 80mm; gap: 3mm; }
  .label { border: 1px dashed rgba(40, 75, 50, 0.35); border-radius: 4px; padding: 4mm 3mm 2mm;
           display: flex; flex-direction: column; align-items: center; text-align: center; background: #fff; }
  .label.empty { border-color: transparent; }
  .club { margin: 0; font-size: 10pt; font-weight: 700; color: ${VERDE}; line-height: 1.15;
          overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
  .aff  { margin: 1mm 0 0; font-size: 6.5pt; color: rgba(0,0,0,0.55); line-height: 1.2; }
  .camp { margin: 0.5mm 0 0; font-size: 6pt; color: rgba(0,0,0,0.38); line-height: 1.2;
          overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
  .off  { color: #9d2f2f; font-weight: 600; }
  .qr   { position: relative; width: 52mm; height: 52mm; margin: 2.5mm 0 auto; }
  .qr .sym { width: 100%; height: 100%; display: block; }
  .logo { position: absolute; inset: 50% auto auto 50%; transform: translate(-50%, -50%);
          width: 12mm; height: 12mm; background: #fff; border-radius: 2mm; padding: 0.8mm;
          display: flex; align-items: center; justify-content: center; }
  .logo img { width: 100%; height: 100%; object-fit: contain; border-radius: 1.2mm; }
  .code { margin: 1.5mm 0 0; font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 6.5pt; letter-spacing: 0.08em; color: rgba(0,0,0,0.38); }
  /* En pantalla la hoja simula el A4 completo —210x297 con los margenes dentro— para que
     la caja de contenido siga midiendo los 190mm de la impresion. Con width:190mm y el
     padding encima, las tres columnas no cabian y la tercera se cortaba en la vista previa. */
  @media screen {
    body { padding: 8mm 0; }
    .sheet { width: 210mm; min-height: 297mm; padding: 10mm; margin-bottom: 8mm;
             box-shadow: 0 8px 28px rgba(38,42,33,0.14); }
  }
  @media print  { body { background: #fff; padding: 0; } }
`;

/**
 * La hoja completa, lista para imprimir: un documento HTML autónomo con todas las
 * etiquetas paginadas de nueve en nueve.
 *
 * Los clubes llegan en el orden que decida quien llama. La página los reparte tal cual,
 * así que ordenarlos por zona antes es lo que permite repartir hojas enteras por zona.
 */
export async function construirHojaEtiquetas({ camp, clubs }) {
  const logoUrl = logoParaImpresion(camp?.logoUrl);
  const campName = camp?.name ?? '';

  const etiquetas = [];
  for (const club of clubs) {
    const qrSvg = await generarQrSvg(construirPayload(camp._id, club._id), { conLogo: Boolean(logoUrl) });
    etiquetas.push(construirEtiqueta({ club, campName, qrSvg, logoUrl }));
  }

  const hojas = [];
  for (let i = 0; i < etiquetas.length; i += POR_HOJA) hojas.push(etiquetas.slice(i, i + POR_HOJA));

  const cuerpo = hojas
    .map((hoja, i) => {
      const huecos = Array.from({ length: POR_HOJA - hoja.length }, () => '<article class="label empty"></article>');
      return `
    <section class="sheet">
      <div class="sheet-head"><span class="meta">Etiquetas QR de clubes · ${i + 1} de ${hojas.length}</span></div>
      <div class="grid">${hoja.join('')}${huecos.join('')}</div>
    </section>`;
    })
    .join('\n');

  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>Etiquetas QR de clubes — ${escapar(campName)}</title>
<style>${ESTILOS}</style>
</head>
<body>
${cuerpo}
</body>
</html>
`;
}

/** Una etiqueta suelta con su hoja de estilos, para la vista previa dentro del panel. */
export async function construirPrevisualizacion({ camp, club }) {
  const logoUrl = logoParaImpresion(camp?.logoUrl);
  const qrSvg = await generarQrSvg(construirPayload(camp._id, club._id), { conLogo: Boolean(logoUrl) });
  return `<style>${ESTILOS}</style><div class="grid" style="grid-template-columns: 1fr">${construirEtiqueta({
    club,
    campName: camp?.name ?? '',
    qrSvg,
    logoUrl,
  })}</div>`;
}

export { POR_HOJA, VERDE };
